import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/ReviewModeration.css";
import { ToastContainer, toast } from "react-toastify";
import { isAdmin } from "../utils/roleUtils";
import "react-toastify/dist/ReactToastify.css";

const HomePageReviews = () => {
    const [allReviews, setAllReviews] = useState([]);
    const [products, setProducts] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [selectedReviewToDelete, setSelectedReviewToDelete] = useState(null);
    const [filterVisible, setFilterVisible] = useState(false);
    const [filterApproved, setFilterApproved] = useState(false);
    const [productFilter, setProductFilter] = useState("all");
    const [showReviewConfirm, setShowReviewConfirm] = useState(false);
    const [selectedReviewId, setSelectedReviewId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const reviewsPerPage = 8;

    useEffect(() => {
        fetchReviews();
        fetchProducts();
    }, []);

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${backend_url}/api/customerreview`);
            setAllReviews(response.data);
        } catch (error) {
            console.error("Error fetching reviews:", error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${backend_url}/allproducts`);
            setProducts(response.data);
        } catch (error) {
            console.error("Error fetching products:", error);
        }
    };

    const productMap = useMemo(() => {
        const map = {};
        products.forEach((p) => { map[p._id] = p; });
        return map;
    }, [products]);

    const productOf = (productId) => productMap[productId];

    const reviewedProducts = useMemo(() => {
        const ids = [...new Set(allReviews.map((r) => r.productId))];
        return ids
            .map((id) => productMap[id])
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allReviews, productMap]);

    const reviews = useMemo(() => {
        let filtered = allReviews;
        if (filterVisible) filtered = filtered.filter((r) => r.showReview);
        if (filterApproved) filtered = filtered.filter((r) => r.showHide);
        if (productFilter !== "all") filtered = filtered.filter((r) => r.productId === productFilter);
        return filtered;
    }, [allReviews, filterVisible, filterApproved, productFilter]);

    useEffect(() => { setCurrentPage(0); }, [filterVisible, filterApproved, productFilter]);

    const counts = {
        total: allReviews.length,
        visible: allReviews.filter((r) => r.showReview).length,
        approved: allReviews.filter((r) => r.showHide).length,
        products: reviewedProducts.length,
    };

    const handleDelete = (id) => {
        setSelectedReviewToDelete(id);
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${backend_url}/api/customerreview/${selectedReviewToDelete}`);
            setShowConfirm(false);
            toast.success("Review deleted successfully");
            fetchReviews();
        } catch (error) {
            toast.error("Failed to delete review");
            console.error("Error deleting review:", error);
        }
    };

    const toggleVisible = async (review) => {
        if (review.showReview) {
            try {
                await axios.patch(`${backend_url}/api/customerreviews/${review._id}/showReview`, { showReview: false });
                toast.success("Review hidden from site");
                fetchReviews();
            } catch (error) {
                toast.error("Failed to hide review");
            }
        } else {
            setSelectedReviewId(review._id);
            setShowReviewConfirm(true);
        }
    };

    const confirmShowReview = async () => {
        try {
            await axios.patch(`${backend_url}/api/customerreviews/${selectedReviewId}/showReview`, { showReview: true });
            toast.success("Review is now visible on site!");
            setShowReviewConfirm(false);
            fetchReviews();
        } catch (error) {
            toast.error("Failed to update review");
        }
    };

    const renderStars = (rating) => {
        const filled = parseInt(rating, 10) || 0;
        return "★".repeat(filled) + "☆".repeat(5 - filled);
    };

    const paginatedReviews = reviews.slice(
        currentPage * reviewsPerPage,
        (currentPage + 1) * reviewsPerPage
    );
    const totalPages = Math.max(1, Math.ceil(reviews.length / reviewsPerPage));

    return (
        <>
            <div className="rm-page">
                <div className="rm-banner">
                    <div className="rm-title-row">
                        <h1 className="rm-title">Review Moderation</h1>
                        <span className="rm-badge">{counts.total} reviews</span>
                    </div>
                    <p className="rm-subtitle">Approve, hide or remove customer reviews per product</p>
                </div>

                <div className="rm-stats-row">
                    <div className="rm-stat-card">
                        <span className="rm-stat-label">Total Reviews</span>
                        <span className="rm-stat-value">{counts.total}</span>
                    </div>
                    <div className="rm-stat-card">
                        <span className="rm-stat-label">Visible on Site</span>
                        <span className="rm-stat-value">{counts.visible}</span>
                    </div>
                    <div className="rm-stat-card">
                        <span className="rm-stat-label">Approved</span>
                        <span className="rm-stat-value">{counts.approved}</span>
                    </div>
                    <div className="rm-stat-card">
                        <span className="rm-stat-label">Products Reviewed</span>
                        <span className="rm-stat-value">{counts.products}</span>
                    </div>
                </div>

                <div className="rm-content">
                    <div className="rm-controls">
                        <select
                            className="rm-product-select"
                            value={productFilter}
                            onChange={(e) => setProductFilter(e.target.value)}
                        >
                            <option value="all">All Products</option>
                            {reviewedProducts.map((p) => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </select>

                        <label className="rm-filter-chip">
                            <input
                                type="checkbox"
                                checked={filterVisible}
                                onChange={(e) => setFilterVisible(e.target.checked)}
                            />
                            Visible only
                        </label>
                        <label className="rm-filter-chip">
                            <input
                                type="checkbox"
                                checked={filterApproved}
                                onChange={(e) => setFilterApproved(e.target.checked)}
                            />
                            Approved only
                        </label>
                    </div>

                    <div className="rm-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Customer</th>
                                    <th>Rating</th>
                                    <th>Review</th>
                                    <th>Photo</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedReviews.length > 0 ? paginatedReviews.map((review) => {
                                    const product = productOf(review.productId);
                                    return (
                                        <tr key={review._id}>
                                            <td>
                                                <div className="rm-product-cell">
                                                    {product?.images?.[0] && (
                                                        <img src={product.images[0]} alt={product.name} className="rm-product-thumb" />
                                                    )}
                                                    <span className="rm-product-name">{product?.name || "Unknown product"}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rm-customer-cell">
                                                    <span className="rm-customer-name">{review.name}</span>
                                                    <span className="rm-customer-email">{review.email}</span>
                                                </div>
                                            </td>
                                            <td><span className="rm-stars">{renderStars(review.rating)}</span></td>
                                            <td>
                                                <div className="rm-review-cell">
                                                    <b>{review.reviewTitle}</b>
                                                    <p>{review.review}</p>
                                                </div>
                                            </td>
                                            <td>
                                                {review.reviewImage ? (
                                                    <img src={review.reviewImage} alt={review.reviewTitle} className="rm-review-img" />
                                                ) : (
                                                    <span className="rm-no-img">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`rm-status-badge ${review.showReview ? "visible" : "hidden"}`}>
                                                    {review.showReview ? "Visible" : "Hidden"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="rm-actions">
                                                    <button
                                                        className={review.showReview ? "rm-btn-hide" : "rm-btn-show"}
                                                        onClick={() => toggleVisible(review)}
                                                    >
                                                        {review.showReview ? "Hide" : "Show"}
                                                    </button>
                                                    {isAdmin() && (
                                                        <button className="rm-btn-delete" onClick={() => handleDelete(review._id)}>
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="rm-empty">No reviews found</div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="rm-pagination">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                                disabled={currentPage === 0}
                            >
                                ‹ Previous
                            </button>
                            <span>Page {currentPage + 1} of {totalPages}</span>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                                disabled={currentPage === totalPages - 1}
                            >
                                Next ›
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showConfirm && (
                <div className="rm-modal-overlay" onClick={() => setShowConfirm(false)}>
                    <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
                        <p>Are you sure you want to delete this review?</p>
                        <div className="rm-modal-actions">
                            <button onClick={confirmDelete} className="rm-btn-delete">Confirm</button>
                            <button onClick={() => setShowConfirm(false)} className="rm-btn-cancel">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {showReviewConfirm && (
                <div className="rm-modal-overlay" onClick={() => setShowReviewConfirm(false)}>
                    <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
                        <p>Show this review on the product page?</p>
                        <div className="rm-modal-actions">
                            <button onClick={confirmShowReview} className="rm-btn-show">Yes, Show It</button>
                            <button onClick={() => setShowReviewConfirm(false)} className="rm-btn-cancel">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </>
    );
};

export default HomePageReviews;
